import { useMaskito } from "@maskito/react";
import { TextField, TextFieldProps } from "../../TextField";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMergeRefs } from "@floating-ui/react";
import { YearlessDateMode, YearlessDate } from "../types";
import { usePrevious } from "../../../internal";
import yearlessDatePlaceholderMask from "./maskito/yearlessDatePlaceholderMask";

export type MaskedYearlessDateInputChange = {
  event: React.ChangeEvent<HTMLInputElement>;
  value: YearlessDate | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
};

export type MaskedYearlessDateInputChangeHandler = (
  change: MaskedYearlessDateInputChange,
) => void;

export type MaskedYearlessDateInputProps = Omit<
  TextFieldProps,
  "onChange" | "value" | "ref" | "showCounter" | "hint"
> & {
  onChange: MaskedYearlessDateInputChangeHandler;
  mode?: YearlessDateMode;
  value?: YearlessDate | null;
  disableHint?: boolean;
};

export type MaskedYearlessDateInput = HTMLInputElement & {
  setValue: (value: YearlessDate | null) => void;
};

/**
 * A masked date input component that allows for the input of a date in a specific format.
 *
 * Provides a `setDate` method that can be used to set the date from outside the component (e.g from a calendar).
 *
 * @internal This component is not intended to be used directly in consumer code.
 *
 * @param props - The props for the MaskedDateInput component.
 * @param props.onChange - The function to call when the date changes.
 * @param props.mode - The mode of the date input.
 */
export const MaskedYearlessDateInput = forwardRef<
  MaskedYearlessDateInput,
  MaskedYearlessDateInputProps
>(({ onChange, mode = "mm/dd", value, disableHint = false, ...props }, ref) => {
  const placeholder = "__/__";
  const [inputValue, setInputValue] = useState(placeholder);
  const { options, removePlaceholder } = yearlessDatePlaceholderMask({
    mode,
    placeholder,
  });
  const maskedInputRef = useMaskito({ options });
  const inputRef = useRef<HTMLInputElement>(null);
  const combinedRef = useMergeRefs([maskedInputRef, inputRef, ref]);
  const previousValue = usePrevious(value);
  const previousMode = usePrevious(mode);

  // Update the input value when the mode changes.
  useEffect(() => {
    if (mode !== previousMode) {
      setInputValue((oldInputValue) =>
        swapMode(oldInputValue, previousMode ?? mode, mode),
      );
    }
  }, [mode, previousMode]);

  // Update the input value when the parent's value changes.
  useEffect(() => {
    if (
      previousValue?.day !== value?.day ||
      previousValue?.month !== value?.month
    ) {
      if (value?.day && value?.month) {
        setInputValue(stringifyYearlessDate(value.day, value.month, mode));
      } else if (
        previousValue !== null &&
        previousValue !== undefined &&
        (value === null || (value?.day === null && value?.month === null))
      ) {
        setInputValue(placeholder);
      } else {
        return;
      }
    }
  }, [value, mode, previousValue]);

  const currentParsedData = useMemo(() => {
    return parseInputValue(inputValue, mode, removePlaceholder);
  }, [inputValue, mode, removePlaceholder]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    const {
      value: v,
      isInputValid,
      isInputEmpty,
    } = parseInputValue(event.target.value, mode, removePlaceholder);

    const isDateDifferent =
      (v === null) !== (currentParsedData.value === null) ||
      v?.day !== currentParsedData.value?.day ||
      v?.month !== currentParsedData.value?.month;

    if (!isDateDifferent) {
      return;
    }

    onChange?.({
      event,
      value: v ?? currentParsedData.value ?? null,
      isInputValid,
      isInputEmpty,
    });
  };

  // Extend the input ref with additional methods.
  // This provides a way to set the input value from outside the component.
  useImperativeHandle(ref, () => {
    const input = inputRef.current;
    if (!input) return null as unknown as MaskedYearlessDateInput;
    return Object.assign(input, {
      setValue: (value: YearlessDate | null) => {
        if (!value) {
          setInputValue(placeholder);
          return;
        }
        setInputValue(
          stringifyYearlessDate(value.day ?? 1, value.month ?? 1, mode),
        );
      },
    });
  }, [mode, placeholder]);

  return (
    <TextField
      ref={combinedRef}
      data-month-value={value?.month}
      data-day-value={value?.day}
      data-input-valid={currentParsedData.isInputValid}
      data-input-empty={currentParsedData.isInputEmpty}
      {...props}
      showCounter={false}
      value={inputValue}
      onChange={handleChange}
      hint={disableHint ? undefined : `Format: ${mode}`}
    />
  );
});

MaskedYearlessDateInput.displayName = "MaskedYearlessDateInput";

function parseInputValue(
  value: string,
  mode: YearlessDateMode,
  removePlaceholder: (value: string) => string,
): {
  value: YearlessDate | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
} {
  const valueMinusPlaceholder = removePlaceholder(value);
  const [segment1, segment2] = valueMinusPlaceholder.split("/");
  const incompleteFirstSegment = segment1?.length !== 2;
  const incompleteSecondSegment = segment2?.length !== 2;
  if (mode === "dd/mm") {
    return {
      value: {
        day: incompleteFirstSegment ? null : parseInt(segment1),
        month: incompleteSecondSegment ? null : parseInt(segment2),
      },
      isInputValid: !incompleteFirstSegment && !incompleteSecondSegment,
      isInputEmpty: valueMinusPlaceholder === "",
    };
  }
  return {
    value: {
      day: incompleteSecondSegment ? null : parseInt(segment2),
      month: incompleteFirstSegment ? null : parseInt(segment1),
    },
    isInputValid: !incompleteFirstSegment && !incompleteSecondSegment,
    isInputEmpty: valueMinusPlaceholder === "",
  };
}

function swapMode(
  inputString: string,
  previousMode: YearlessDateMode,
  mode: YearlessDateMode,
) {
  const { day, month } = divideSegments(inputString, previousMode);
  return stringifyYearlessDate(day, month, mode);
}

function divideSegments(
  value: string,
  mode: YearlessDateMode,
): {
  day: string;
  month: string;
} {
  const [segment1, segment2] = value.split("/");
  if (mode === "dd/mm") {
    return { day: segment1, month: segment2 };
  }
  return { day: segment2, month: segment1 };
}

function stringifyYearlessDate(
  day: number | string,
  month: number | string,
  mode: YearlessDateMode,
) {
  const dd = day.toString().padStart(2, "0");
  const mm = month.toString().padStart(2, "0");
  if (mode === "dd/mm") {
    return `${dd}/${mm}`;
  }
  return `${mm}/${dd}`;
}
